import { memo, useCallback } from 'react';

import { CheckIcon, CopyIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';

import { useCopied } from '~/hooks/useCopied';
import { useCopyTextToClipboard } from '~/hooks/useCopyTextToClipboard';

export type WorkspaceAgentMessageFeedbackCopyButtonProps = {
  copyText: string;
};

export const WorkspaceAgentMessageFeedbackCopyButton = memo(
  ({ copyText }: WorkspaceAgentMessageFeedbackCopyButtonProps) => {
    const { copyTextToClipboard } = useCopyTextToClipboard();
    const { copied, markCopied } = useCopied();

    const handleCopy = useCallback(() => {
      void (async () => {
        const { clipboard } = navigator;

        if (!clipboard?.writeText) {
          await copyTextToClipboard({ errorMessage: 'Failed to copy text', text: copyText });
          return;
        }

        try {
          await clipboard.writeText(copyText);
          markCopied();
        } catch {
          await copyTextToClipboard({ errorMessage: 'Failed to copy text', text: copyText });
        }
      })();
    }, [copyText, copyTextToClipboard, markCopied]);

    return (
      <>
        <Tooltip label={copied ? 'Copied!' : 'Copy'}>
          <IconButton
            adsIcon
            appearance="ghost"
            aria-label={copied ? 'Copied' : 'Copy'}
            className="focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-8"
            color="gray"
            data-testid={
              copied ? 'WORKSPACE_AGENT_MESSAGE_FEEDBACK_COPY_CONFIRMATION' : 'WORKSPACE_AGENT_MESSAGE_FEEDBACK_COPY'
            }
            icon={copied ? CheckIcon : CopyIcon}
            label={copied ? 'Copied' : 'Copy'}
            onClick={handleCopy}
            size="extra-small"
          />
        </Tooltip>
        <span aria-live="polite" className="sr-only">
          {copied ? 'Copied' : ''}
        </span>
      </>
    );
  },
);

WorkspaceAgentMessageFeedbackCopyButton.displayName = 'WorkspaceAgentMessageFeedbackCopyButton';
